/**
 * The agent's prose, rendered the way Cursor renders a reply: 13px body with
 * comfortable leading, real bullets and numbers, tight paragraph rhythm, code
 * blocks with a header and copy button, and a caret at the tail while the
 * text is still streaming.
 *
 * Wraps Streamdown. `streaming` switches it into incremental mode (blocks are
 * parsed as they arrive, unfinished markdown is closed for display) and shows
 * the caret; a finished message renders statically, which is cheaper.
 *
 * `PROSE_COMPONENTS` spells out the list styles instead of relying on
 * Streamdown's own classes: Tailwind's preflight strips `list-style`, and a
 * bullet that depends on a class the build did not see is a bullet that is
 * not there.
 */
import { memo, type ComponentProps, type FC } from "react";
import { Streamdown } from "streamdown";
import { cjk } from "@streamdown/cjk";
import { code } from "@streamdown/code";

import { cn } from "@/lib/utils";

import { ChatLink, unwrapCodeLinks } from "./ChatLink";

type Components = NonNullable<ComponentProps<typeof Streamdown>["components"]>;

type ListProps = ComponentProps<"ul"> & { node?: unknown };
type OrderedListProps = ComponentProps<"ol"> & { node?: unknown };
type ItemProps = ComponentProps<"li"> & { node?: unknown };
type ParagraphProps = ComponentProps<"p"> & { node?: unknown };
type HeadingProps = ComponentProps<"h1"> & { node?: unknown };
type QuoteProps = ComponentProps<"blockquote"> & { node?: unknown };
type RuleProps = ComponentProps<"hr"> & { node?: unknown };

const UnorderedList: FC<ListProps> = ({ className, node: _n, ...rest }) => (
  <ul
    className={cn(
      "my-2 list-disc space-y-1 pl-5 marker:text-muted-foreground",
      className
    )}
    {...rest}
  />
);

const OrderedList: FC<OrderedListProps> = ({
  className,
  node: _n,
  ...rest
}) => (
  <ol
    className={cn(
      "my-2 list-decimal space-y-1 pl-5 marker:text-muted-foreground",
      className
    )}
    {...rest}
  />
);

const ListItem: FC<ItemProps> = ({ className, node: _n, ...rest }) => (
  <li className={cn("pl-0.5 [&>p]:my-0", className)} {...rest} />
);

const Paragraph: FC<ParagraphProps> = ({ className, node: _n, ...rest }) => (
  <p className={cn("my-2 first:mt-0 last:mb-0", className)} {...rest} />
);

const heading = (
  Tag: "h1" | "h2" | "h3" | "h4",
  size: string
): FC<HeadingProps> => {
  const Heading: FC<HeadingProps> = ({ className, node: _n, ...rest }) => (
    <Tag
      className={cn(
        "mt-4 mb-1.5 font-semibold tracking-tight first:mt-0",
        size,
        className
      )}
      {...rest}
    />
  );
  Heading.displayName = `Prose${Tag.toUpperCase()}`;
  return Heading;
};

const Quote: FC<QuoteProps> = ({ className, node: _n, ...rest }) => (
  <blockquote
    className={cn(
      "my-2 border-l border-line-3 pl-3 text-muted-foreground",
      className
    )}
    {...rest}
  />
);

const Rule: FC<RuleProps> = ({ className, node: _n, ...rest }) => (
  <hr className={cn("my-3 border-border", className)} {...rest} />
);

/**
 * Module-level so its identity is stable: Streamdown re-parses when
 * `components` changes.
 */
export const PROSE_COMPONENTS = {
  a: ChatLink,
  ul: UnorderedList,
  ol: OrderedList,
  li: ListItem,
  p: Paragraph,
  h1: heading("h1", "text-md"),
  h2: heading("h2", "text-md"),
  h3: heading("h3", "text-base"),
  h4: heading("h4", "text-base"),
  blockquote: Quote,
  hr: Rule
} as unknown as Components;

const PLUGINS = { cjk, code };

/** GitHub's themes, the ones Cursor's code blocks are closest to. */
const SHIKI_THEME: [string, string] = ["github-light", "github-dark"];

const CONTROLS = {
  code: { copy: true, download: false },
  table: { copy: true, download: false, fullscreen: false },
  mermaid: false
} as const;

type Props = {
  children: string;
  /** Still arriving: parse incrementally and show the caret. */
  streaming?: boolean;
  className?: string;
};

const ChatProseView: FC<Props> = ({
  children,
  streaming = false,
  className
}) => (
  <div
    className={cn(
      "chat-prose min-w-0 break-words",
      "[&_code:not(pre_code)]:rounded [&_code:not(pre_code)]:bg-muted [&_code:not(pre_code)]:px-1 [&_code:not(pre_code)]:py-px [&_code:not(pre_code)]:font-mono [&_code:not(pre_code)]:text-[0.92em]",
      "[&_table]:text-sm",
      "text-base leading-[1.6]",
      className
    )}
    data-slot="chat-prose"
    data-streaming={streaming ? "true" : undefined}
  >
    <Streamdown
      mode={streaming ? "streaming" : "static"}
      isAnimating={streaming}
      caret={streaming ? "block" : undefined}
      parseIncompleteMarkdown={streaming}
      components={PROSE_COMPONENTS}
      plugins={PLUGINS}
      shikiTheme={SHIKI_THEME}
      controls={CONTROLS}
      codeBlockMaxHeight={320}
      lineNumbers={false}
      className="size-full [&>*:first-child]:mt-0 [&>*:last-child]:mb-0"
    >
      {unwrapCodeLinks(children)}
    </Streamdown>
  </div>
);

export const ChatProse = memo(
  ChatProseView,
  (prev, next) =>
    prev.children === next.children &&
    prev.streaming === next.streaming &&
    prev.className === next.className
);
ChatProse.displayName = "ChatProse";
